import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { Trash2, Mail, Phone, Calendar } from "lucide-react";

const STATUS_OPTIONS = ["new", "read", "archived"];

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inquiries");
      setInquiries(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  const updateStatus = async (id, status) => {
    await api.patch(`/inquiries/${id}`, { status });
    setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    await api.delete(`/inquiries/${id}`);
    setInquiries((prev) => prev.filter((i) => i.id !== id));
    if (selected?.id === id) setSelected(null);
  };

  return (
    <AdminLayout>
      <div data-testid="admin-inquiries-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#1E5BB8] mb-2">
              Admin / Inquiries
            </div>
            <h1 className="font-display text-4xl md:text-5xl uppercase leading-none">
              Inbox
            </h1>
          </div>
          <div className="font-mono text-sm" data-testid="inquiries-count">
            <span className="font-bold text-2xl">{inquiries.length}</span>{" "}
            <span className="uppercase tracking-[0.2em] text-xs text-[#091526]/60">total</span>
          </div>
        </div>

        {loading ? (
          <div className="font-mono">Loading…</div>
        ) : inquiries.length === 0 ? (
          <div className="border-2 border-[#091526] bg-white p-12 text-center" data-testid="inquiries-empty">
            <p className="font-mono text-sm uppercase tracking-[0.2em] text-[#091526]/60">
              No inquiries yet.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* LIST */}
            <div className="lg:col-span-7">
              <div className="border-2 border-[#091526]">
                {inquiries.map((inq, i) => (
                  <button
                    key={inq.id}
                    onClick={() => setSelected(inq)}
                    data-testid={`inquiry-row-${inq.id}`}
                    className={`w-full text-left p-5 ${
                      i < inquiries.length - 1 ? "border-b-2 border-[#091526]" : ""
                    } ${selected?.id === inq.id ? "bg-[#1E5BB8]/20" : "bg-white hover:bg-[#F4F4F5]"}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-display text-xl">{inq.name}</span>
                      <StatusPill status={inq.status} />
                    </div>
                    <div className="font-mono text-xs text-[#091526]/70 mb-2 truncate">{inq.email}</div>
                    <div className="text-sm text-[#091526]/80 line-clamp-2">{inq.message}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#091526]/50 mt-2">
                      {new Date(inq.created_at).toLocaleString()}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* DETAIL */}
            <div className="lg:col-span-5">
              {selected ? (
                <div className="border-2 border-[#091526] bg-white p-6 sticky top-6" data-testid="inquiry-detail">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#091526]/60 mb-1">From</div>
                      <h3 className="font-display text-3xl">{selected.name}</h3>
                    </div>
                    <button
                      onClick={() => deleteInquiry(selected.id)}
                      className="border-2 border-[#091526] p-2 hover:bg-red-600 hover:text-white hover:border-red-600"
                      data-testid="inquiry-delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="space-y-3 text-sm mb-6">
                    <Row icon={<Mail size={14} />} label={selected.email} />
                    {selected.phone && <Row icon={<Phone size={14} />} label={selected.phone} />}
                    {selected.size_interest && (
                      <Row icon={<span className="font-mono text-xs">SIZE</span>} label={selected.size_interest} />
                    )}
                    <Row icon={<Calendar size={14} />} label={new Date(selected.created_at).toLocaleString()} />
                  </div>

                  <div className="border-t-2 border-[#091526] pt-5 mb-6">
                    <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#091526]/60 mb-2">Message</div>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="inquiry-message">
                      {selected.message}
                    </p>
                  </div>

                  <div>
                    <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#091526]/60 mb-2">Status</div>
                    <div className="flex gap-2">
                      {STATUS_OPTIONS.map((s) => (
                        <button
                          key={s}
                          onClick={() => updateStatus(selected.id, s)}
                          data-testid={`inquiry-status-${s}`}
                          className={`flex-1 py-2 font-mono text-xs uppercase tracking-[0.2em] font-bold border-2 border-[#091526] ${
                            selected.status === s ? "bg-[#1E5BB8]" : "bg-white hover:bg-[#F4F4F5]"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <a
                    href={`mailto:${selected.email}?subject=Re: Pile Glide Pro inquiry`}
                    data-testid="inquiry-reply"
                    className="mt-6 inline-flex items-center gap-2 bg-[#091526] text-[#F4F4F5] font-bold uppercase text-xs tracking-[0.2em] px-5 py-3 border-2 border-[#091526] hover:bg-[#1E5BB8] hover:text-[#091526]"
                  >
                    <Mail size={14} /> Reply via email
                  </a>
                </div>
              ) : (
                <div className="border-2 border-dashed border-[#091526]/40 p-10 text-center">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#091526]/50">
                    Select an inquiry to view
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function Row({ icon, label }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-[#1E5BB8]">{icon}</span>
      <span className="font-mono text-xs">{label}</span>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    new: "bg-[#1E5BB8] text-[#091526]",
    read: "bg-[#091526] text-[#F4F4F5]",
    archived: "bg-[#F4F4F5] text-[#091526] border-2 border-[#091526]",
  };
  return (
    <span className={`px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] font-bold ${map[status] || ""}`}>
      {status}
    </span>
  );
}
